import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Trash2, Eye, Plus, Building2, Lock, GitCompareArrows } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getLocationLabel } from '../lib/location-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Projects() {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchQuota();
  }, []);

  const fetchQuota = async () => {
    try {
      const response = await axios.get(`${API}/projects/quota`, {
        headers: getAuthHeader()
      });
      setQuota(response.data);
    } catch (error) {
      console.error('Error fetching quota:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeader()
      });
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Erreur lors du chargement des projets');
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await axios.delete(`${API}/projects/${projectToDelete}`, {
        headers: getAuthHeader()
      });
      toast.success('Projet supprimé avec succès');
      setProjects(projects.filter(p => p.id !== projectToDelete));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchQuota();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Erreur lors de la suppression du projet');
    }
  };

  const quotaReached = quota && !quota.can_create;

  const handleNewProject = () => {
    if (quotaReached) {
      if (user?.plan === 'free') {
        toast.error('Votre simulation Découverte a été utilisée. Passez à un abonnement pour continuer.');
      } else {
        toast.error(`Crédits mensuels utilisés (${quota.current}/${quota.max}). Passez à un plan supérieur !`);
      }
      navigate('/pricing');
    } else {
      navigate('/calculator');
    }
  };

  const openDeleteDialog = (projectId) => {
    setProjectToDelete(projectId);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#005F73] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-stone-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-sand py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            data-testid="back-to-home-btn"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 text-[#005F73] hover:text-[#005F73]/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour à l'accueil
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-[#005F73] mb-2 tracking-tight">
                Mes Projets
              </h1>
              <p className="text-base sm:text-lg text-stone-600">
                {projects.length} projet{projects.length > 1 ? 's' : ''} actif{projects.length > 1 ? 's' : ''}
                {quota && user?.plan !== 'free' && quota.max !== -1 && (
                  <span className={`ml-2 text-sm font-medium ${quotaReached ? 'text-red-500' : 'text-[#0A9396]'}`}>
                    ({quota.current}/{quota.max} ce mois-ci)
                  </span>
                )}
              </p>
            </div>
            
            <Button
              data-testid="btn-new-project"
              onClick={handleNewProject}
              disabled={quotaReached}
              className={`rounded-full px-6 py-6 shadow-lg w-full sm:w-auto ${
                quotaReached
                  ? 'bg-stone-300 text-stone-500 cursor-not-allowed hover:bg-stone-300'
                  : 'bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white'
              }`}
            >
              {quotaReached ? (
                <>
                  <Lock className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Quota atteint
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Nouveau projet
                </>
              )}
            </Button>

            {projects.length >= 2 && (
              <Button
                data-testid="btn-go-compare"
                onClick={() => navigate('/compare')}
                variant="outline"
                className="rounded-full px-6 py-6 border-2 border-[#005F73] text-[#005F73] hover:bg-[#005F73]/5 w-full sm:w-auto"
              >
                <GitCompareArrows className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Comparer
              </Button>
            )}
          </div>

          {quotaReached && (
            <div data-testid="quota-banner" className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-amber-800">
                {user?.plan === 'free'
                  ? <>Vous avez utilisé votre <strong>simulation Découverte</strong>. Pour continuer à simuler vos investissements, choisissez un abonnement adapté à vos besoins.</>
                  : <>Vous avez utilisé vos <strong>{quota.max} crédits mensuels</strong> du plan <strong>{quota.plan_name}</strong>. Passez à un plan supérieur pour créer plus de projets.</>
                }
              </p>
              <Button
                data-testid="btn-upgrade-plan"
                onClick={() => navigate('/pricing')}
                className="rounded-full px-6 bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white whitespace-nowrap w-full sm:w-auto"
              >
                Voir les abonnements
              </Button>
            </div>
          )}
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div 
            data-testid="empty-state"
            className="bg-white rounded-2xl border border-stone-100 p-12 text-center shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="w-20 h-20 rounded-full bg-[#005F73]/10 flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-[#005F73]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-[#005F73] mb-3">Aucun projet pour le moment</h3>
            <p className="text-stone-600 mb-6 max-w-md mx-auto leading-relaxed">
              Créez votre premier projet pour commencer à analyser la rentabilité de vos investissements immobiliers.
            </p>
            <Button
              data-testid="btn-create-first-project"
              onClick={() => navigate('/calculator')}
              className="rounded-full px-8 py-6 text-lg bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Créer mon premier projet
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project) => (
              <div
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)] hover:shadow-[0_8px_30px_-4px_rgba(0,95,115,0.2)] transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#005F73]/10 flex items-center justify-center group-hover:bg-[#005F73]/20 transition-all">
                    <Building2 className="w-6 h-6 text-[#005F73]" strokeWidth={1.5} />
                  </div>
                  <button
                    data-testid={`btn-delete-${project.id}`}
                    onClick={() => openDeleteDialog(project.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-5 h-5 text-stone-400 hover:text-red-500" strokeWidth={1.5} />
                  </button>
                </div>

                <h3 className="text-xl font-semibold text-[#005F73] mb-2" data-testid={`project-name-${project.id}`}>
                  {project.name}
                </h3>
                
                <div className="space-y-2 mb-4 text-sm text-stone-600">
                  <p><span className="font-medium">Type:</span> {project.property_type}</p>
                  <p><span className="font-medium">Localisation:</span> {getLocationLabel(project.location, project.department)}</p>
                  <p><span className="font-medium">Prix:</span> {project.purchase_price.toLocaleString()}€</p>
                  <p><span className="font-medium">Loyer:</span> {project.monthly_rent.toLocaleString()}€/mois</p>
                </div>

                <Button
                  data-testid={`btn-view-${project.id}`}
                  onClick={() => navigate(`/results/${project.id}`)}
                  className="w-full rounded-xl bg-[#005F73] hover:bg-[#005F73]/90 transition-all"
                >
                  <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Voir les résultats
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete"
              onClick={handleDeleteProject}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
